import { type Edge, MarkerType, type Node } from '@xyflow/react'

import type { WorkflowGraph, WorkflowGraphEdge, WorkflowGraphNode, WorkflowStepCatalogItem } from '../../api/workflowRuns'
import { workflowStepTypeLabel } from './workflowDisplayCatalog'

export const WORKFLOW_STEP_DRAG_MIME = 'application/glyphic-workflow-step-type'

export const WORKFLOW_NODE_WIDTH = 260
export const WORKFLOW_X_GAP = 320
export const WORKFLOW_Y_BASE = 128

export type WorkflowNodeGraphSlice = Pick<WorkflowGraph, 'nodes' | 'edges'>

export type WorkflowNodeData = {
  node: WorkflowGraphNode
  typeLabel: string
  catalogItem?: WorkflowStepCatalogItem
  graph?: WorkflowNodeGraphSlice
  /** 平台模型目录变更时递增，驱动节点属性标签刷新 */
  modelCatalogKey?: string
  angular?: boolean
  canEdit?: boolean
  deletable?: boolean
  onDelete?: (nodeId: string) => void
}

export function toFlowNodes(
  graphNodes: WorkflowGraphNode[],
  catalogByType?: Map<string, WorkflowStepCatalogItem>,
  graphEdges: WorkflowGraphEdge[] = [],
): Node<WorkflowNodeData>[] {
  const graph: WorkflowNodeGraphSlice = { nodes: graphNodes, edges: graphEdges }
  return graphNodes.map((node, index) => {
    const x = Number(node.position?.x ?? WORKFLOW_X_GAP * index)
    const y = Number(node.position?.y ?? WORKFLOW_Y_BASE)
    return {
      id: node.nodeId,
      type: 'workflowNode',
      position: { x: Number.isFinite(x) ? x : WORKFLOW_X_GAP * index, y: Number.isFinite(y) ? y : WORKFLOW_Y_BASE },
      data: {
        node,
        typeLabel: workflowStepTypeLabel(node.type),
        catalogItem: catalogByType?.get(node.type),
        graph,
      },
      draggable: true,
      selectable: true,
    }
  })
}

export function toFlowEdges(graphEdges: WorkflowGraphEdge[]): Edge[] {
  return graphEdges.map((edge) => ({
    id: edge.edgeId,
    source: edge.sourceNodeId,
    sourceHandle: edge.sourcePort || undefined,
    target: edge.targetNodeId,
    targetHandle: edge.targetPort || undefined,
    data: { route: edge.route || routeFromSourcePort(edge.sourcePort) },
    type: 'smoothstep',
    animated: false,
    style: { stroke: 'color-mix(in srgb, var(--accent) 55%, var(--line))', strokeWidth: 2.2 },
    markerEnd: { type: MarkerType.ArrowClosed, color: 'var(--accent)' },
  }))
}

function routeFromSourcePort(sourcePort: string | null | undefined): string {
  const port = String(sourcePort ?? '').trim()
  return port === 'pass' || port === 'fail' || port === 'body' || port === 'done' ? port : ''
}

export function normalizeEdgeId(sourceNodeId: string, targetNodeId: string, sourcePort = 'out', targetPort = 'main') {
  return `${sourceNodeId}:${sourcePort}->${targetNodeId}:${targetPort}`
}

export function graphFromNodesAndEdges(nodes: WorkflowGraphNode[], edges: WorkflowGraphEdge[]): WorkflowGraph {
  return {
    nodes,
    edges,
    inputSchema: { fields: [] },
    outputSchema: {},
  }
}

export function addNodeAtPosition(
  nodes: WorkflowGraphNode[],
  type: string,
  name: string,
  defaultConfig: Record<string, unknown>,
  x: number,
  y: number,
): WorkflowGraphNode[] {
  const nextId = suggestNodeId(type, nodes)
  return [
    ...nodes,
    {
      nodeId: nextId,
      type,
      name,
      description: '',
      position: { x, y },
      config: defaultConfig,
    },
  ]
}

export function suggestNodeId(type: string, nodes: WorkflowGraphNode[]): string {
  const base = type.replace(/[^a-zA-Z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'node'
  let idx = 1
  while (nodes.some((node) => node.nodeId === `${base}_${idx}`)) {
    idx += 1
  }
  return `${base}_${idx}`
}

export function updateNode(
  nodes: WorkflowGraphNode[],
  nodeId: string,
  patch: Partial<WorkflowGraphNode>,
): WorkflowGraphNode[] {
  return nodes.map((node) => (node.nodeId === nodeId ? { ...node, ...patch } : node))
}

export function removeNodeAndRelatedEdges(
  nodes: WorkflowGraphNode[],
  edges: WorkflowGraphEdge[],
  nodeId: string,
): { nodes: WorkflowGraphNode[]; edges: WorkflowGraphEdge[] } {
  return {
    nodes: nodes.filter((node) => node.nodeId !== nodeId),
    edges: edges.filter((edge) => edge.sourceNodeId !== nodeId && edge.targetNodeId !== nodeId),
  }
}

/** 仅反映画布拓扑（id / type / name / 连线），不含 position 与 config，避免拖拽后整图重灌 */
export function workflowGraphTopologyKey(
  nodes: WorkflowGraphNode[],
  edges: WorkflowGraphEdge[],
): string {
  const nodePart = nodes.map((node) => `${node.nodeId}|${node.type}|${node.name}`).join(';')
  const edgePart = edges
    .map((edge) => `${edge.edgeId}|${edge.sourceNodeId}|${edge.sourcePort}|${edge.targetNodeId}|${edge.targetPort}`)
    .join(';')
  return `${nodePart}::${edgePart}`
}

export function flowEdgesToGraphEdges(flowEdges: Edge[]): WorkflowGraphEdge[] {
  return flowEdges.map((edge) => ({
    edgeId: edge.id,
    sourceNodeId: edge.source,
    sourcePort: edge.sourceHandle ?? 'out',
    targetNodeId: edge.target,
    targetPort: edge.targetHandle ?? 'main',
    route: typeof edge.data?.route === 'string' ? edge.data.route : routeFromSourcePort(edge.sourceHandle),
    condition: '',
  }))
}

export function mergeFlowNodesWithGraph(
  flowNodes: Node<WorkflowNodeData>[],
  graphNodes: WorkflowGraphNode[],
): WorkflowGraphNode[] {
  const graphById = new Map(graphNodes.map((node) => [node.nodeId, node]))
  const merged: WorkflowGraphNode[] = []
  for (const flowNode of flowNodes) {
    const graphNode = graphById.get(flowNode.id)
    if (!graphNode) continue
    merged.push({
      ...graphNode,
      position: { x: flowNode.position.x, y: flowNode.position.y },
    })
  }
  return merged
}
